import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  Download, 
  Share2, 
  Smartphone, 
  FolderDown,
  Copy,
  Check,
  ExternalLink,
  Film,
  FileText,
  MessageCircle,
  Send,
  ImageIcon,
  Info
} from "lucide-react";
import { toast } from "sonner";
import { BASE_URL, getVideoUrl, getAssetUrl } from "../lib/api";

// Social media icons
const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export default function VideoDownloadDialog({ 
  open, 
  onOpenChange, 
  video,
  onDownloadComplete 
}) {
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showIOSTip, setShowIOSTip] = useState(false);

  if (!video) return null;

  const token = localStorage.getItem("auth_token");
  
  // Always use the download endpoint - it handles finding the video from various sources
  // (local file, S3, cloud_video_url) and provides proper streaming
  const authDownloadUrl = getVideoUrl(`/api/videos/${video.id}/download`, token);
  
  // Public share URL (no auth required)
  const publicShareUrl = getAssetUrl(`/api/share/${video.id}`);
  
  const subtitlesUrl = video.srt_url 
    ? getVideoUrl(`/api/videos/${video.id}/subtitles/download`, token)
    : null;

  // Check device type
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);

  // Save to Photos (Mobile) - Uses native share sheet with file
  const handleSaveToPhotos = async () => {
    setDownloading(true);
    let toastId = null;
    
    try {
      toastId = toast.loading("Preparing video for save...");
      
      const authToken = localStorage.getItem("auth_token");
      const downloadUrl = `${BASE_URL}/api/videos/${video.id}/download?token=${authToken}`;
      
      // For both iOS and Android: Download the video file first, then share it
      // This is the only way to get "Save to Photos" to work properly
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'video/mp4, */*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error("Downloaded video is empty");
      }
      
      const filename = `${video.title?.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'video'}.mp4`;
      const file = new File([blob], filename, { type: 'video/mp4' });
      
      // Check if we can share files (required for Save to Photos)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        if (toastId) toast.dismiss(toastId);
        
        // Show iOS-specific tip before sharing
        if (isIOS) {
          setShowIOSTip(true);
        }
        
        await navigator.share({
          files: [file],
          title: video.title
        });
        
        toast.success(
          isIOS 
            ? "Shared! Tap 'Save Video' to save to Photos" 
            : "Shared! Select 'Save to Gallery' to save",
          { duration: 5000 }
        );
        onDownloadComplete?.();
      } else {
        // File sharing not supported, fall back to download link
        if (toastId) toast.dismiss(toastId);
        
        // For iOS Safari that doesn't support file sharing, open the video directly
        if (isIOS) {
          window.open(downloadUrl, '_blank');
          toast.success(
            "Video opened! Long-press on the video and tap 'Save to Photos'",
            { duration: 6000 }
          );
        } else {
          toast.info("File sharing not available. Starting download instead...", { duration: 3000 });
          await handleStandardDownload();
        }
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.info("Share canceled", { duration: 2000 });
      } else {
        console.error("Save to photos error:", error);
        
        // Offer fallback
        const authToken = localStorage.getItem("auth_token");
        const downloadUrl = `${BASE_URL}/api/videos/${video.id}/download?token=${authToken}`;
        
        if (isIOS) {
          toast.error(
            "Could not save directly. Opening video...",
            { duration: 3000 }
          );
          setTimeout(() => {
            window.open(downloadUrl, '_blank');
            toast.info(
              "Long-press on the video and tap 'Save to Photos'",
              { duration: 5000 }
            );
          }, 1500);
        } else {
          toast.error(
            `Could not save to photos: ${error.message}. Please use 'Download Video' instead.`,
            { duration: 4000 }
          );
        }
      }
    } finally {
      if (toastId) toast.dismiss(toastId);
      setDownloading(false);
      setShowIOSTip(false);
    }
  };
  
  // Helper function to share video blob
  const shareVideoBlob = async (blob, toastId) => {
    if (blob.size === 0) {
      throw new Error("Downloaded video is empty");
    }
    
    const filename = `${video.title.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'video'}.mp4`;
    const file = new File([blob], filename, { type: 'video/mp4' });
    
    // Check if we can share files
    if (navigator.canShare({ files: [file] })) {
      if (toastId) toast.dismiss(toastId);
      
      // Show iOS-specific tip before sharing
      if (isIOS) {
        setShowIOSTip(true);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      await navigator.share({
        files: [file],
        title: video.title
      });
      
      toast.success(
        isIOS 
          ? "Shared! Tap 'Save Video' to save to Photos" 
          : "Shared! Select 'Save to Gallery' to save",
        { duration: 5000 }
      );
      onDownloadComplete?.();
    } else {
      // File sharing not supported, fall back to download
      if (toastId) toast.dismiss(toastId);
      toast.info("File sharing not available. Starting download instead...", { duration: 3000 });
      await handleStandardDownload();
    }
  };

  // Standard download to Downloads folder (for video owner)
  const handleStandardDownload = async () => {
    setDownloading(true);
    let toastId = null;
    
    try {
      // For external URLs, open in new tab
      if (video.video_url?.startsWith('http')) {
        window.open(video.video_url, '_blank');
        toast.success("Opening video in new tab", { duration: 3000 });
        return;
      }

      toastId = toast.loading("Downloading video...");

      // Fetch the video (token is already in the URL)
      const response = await fetch(authDownloadUrl, {
        method: 'GET',
        mode: 'cors'
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      
      const blob = await response.blob();
      const videoBlob = new Blob([blob], { type: 'video/mp4' });
      const blobUrl = window.URL.createObjectURL(videoBlob);
      
      const filename = `${video.title.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'video'}.mp4`;
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      if (toastId) toast.dismiss(toastId);
      toast.success(
        isMobile 
          ? "Video downloaded! Check your Downloads folder or Files app" 
          : "Video saved to Downloads!",
        { duration: 4000 }
      );
      onDownloadComplete?.();
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.message || "Failed to download video", { duration: 4000 });
    } finally {
      if (toastId) toast.dismiss(toastId);
      setDownloading(false);
    }
  };

  // Native share - shares the actual video file or link
  const handleNativeShare = async () => {
    try {
      if (!navigator.share) {
        toast.error("Sharing not supported on this device", { duration: 3000 });
        return;
      }

      // Share the public download URL
      await navigator.share({
        title: video.title,
        text: `Check out this video: ${video.title}`,
        url: publicShareUrl
      });
      toast.success("Link shared!", { duration: 3000 });
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Share error:", error);
        toast.error("Failed to share", { duration: 3000 });
      }
    }
  };

  // Copy public share link
  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(publicShareUrl);
      setCopied(true);
      toast.success("Download link copied! Anyone with this link can download the video.", { duration: 3000 });
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = publicShareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Download link copied!", { duration: 3000 });
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // Share to specific platforms
  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`Check out this video: ${video.title}\n${publicShareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareToTelegram = () => {
    const text = encodeURIComponent(`Check out this video: ${video.title}`);
    const url = encodeURIComponent(publicShareUrl);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
  };

  // Download subtitles
  const handleDownloadSubtitles = async () => {
    if (!subtitlesUrl) {
      toast.error("No subtitles available for this video", { duration: 3000 });
      return;
    }
    
    try {
      const response = await fetch(subtitlesUrl);
      if (!response.ok) throw new Error("Failed to download subtitles");
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const filename = `${video.title.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'video'}_subtitles.srt`;
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      toast.success("Subtitles downloaded!", { duration: 3000 });
    } catch (error) {
      toast.error("Failed to download subtitles", { duration: 3000 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border/50">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Film className="w-5 h-5 text-[#00F0FF]" />
            Save & Share Video
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Download or share &quot;{video.title}&quot;
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* Primary for Mobile: Save to Photos */}
          {isMobile && navigator.share && navigator.canShare && (
            <Button
              onClick={handleSaveToPhotos}
              disabled={downloading}
              className="w-full justify-start gap-3 h-14 bg-gradient-to-r from-[#00FF94]/20 to-[#00FF94]/10 hover:from-[#00FF94]/30 hover:to-[#00FF94]/20 border border-[#00FF94]/30 text-white"
              data-testid="save-to-photos-btn"
            >
              <ImageIcon className="w-5 h-5 text-[#00FF94]" />
              <div className="text-left flex-1">
                <div className="font-medium">Save to Photos</div>
                <div className="text-xs text-muted-foreground">
                  {isIOS 
                    ? "Save directly to your Camera Roll" 
                    : "Save to your phone's Gallery"}
                </div>
              </div>
              {downloading && <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />}
            </Button>
          )}

          {/* Download Video */}
          <Button
            onClick={handleStandardDownload}
            disabled={downloading}
            className={`w-full justify-start gap-3 h-14 ${
              isMobile && navigator.share && navigator.canShare
                ? "bg-secondary/50 hover:bg-secondary/70 border border-border/30"
                : "bg-gradient-to-r from-[#00F0FF]/20 to-[#00F0FF]/10 hover:from-[#00F0FF]/30 hover:to-[#00F0FF]/20 border border-[#00F0FF]/30"
            } text-white`}
            data-testid="download-video-btn"
          >
            <FolderDown className={`w-5 h-5 ${isMobile && navigator.share && navigator.canShare ? "text-muted-foreground" : "text-[#00F0FF]"}`} />
            <div className="text-left flex-1">
              <div className="font-medium">Download Video</div>
              <div className="text-xs text-muted-foreground">
                Save MP4 to {isMobile ? "Downloads folder" : "your Downloads folder"}
              </div>
            </div>
            {downloading && !isMobile && <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />}
          </Button>

          {/* Mobile: Share Link */}
          {isMobile && navigator.share && (
            <Button
              onClick={handleNativeShare}
              variant="outline"
              className="w-full justify-start gap-3 h-12 border-[#FF0055]/30 hover:bg-[#FF0055]/10 text-white"
              data-testid="share-native-btn"
            >
              <Share2 className="w-5 h-5 text-[#FF0055]" />
              <div className="text-left">
                <div className="font-medium">Share Link</div>
                <div className="text-xs text-muted-foreground">
                  Send download link via any app
                </div>
              </div>
            </Button>
          )}

          {/* Quick Share Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={shareToWhatsApp}
              variant="outline"
              className="justify-center gap-2 h-11 border-[#25D366]/30 hover:bg-[#25D366]/10 text-[#25D366]"
              data-testid="share-whatsapp-btn"
            >
              <WhatsAppIcon />
              WhatsApp
            </Button>
            <Button
              onClick={shareToTelegram}
              variant="outline"
              className="justify-center gap-2 h-11 border-[#0088cc]/30 hover:bg-[#0088cc]/10 text-[#0088cc]"
              data-testid="share-telegram-btn"
            >
              <TelegramIcon />
              Telegram
            </Button>
          </div>

          {/* Copy Share Link */}
          <Button
            onClick={handleCopyShareLink}
            variant="outline"
            className="w-full justify-start gap-3 h-12 border-border/50 hover:bg-secondary/50"
            data-testid="copy-share-link-btn"
          >
            {copied ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <Copy className="w-5 h-5 text-muted-foreground" />
            )}
            <div className="text-left flex-1">
              <div className="font-medium">{copied ? "Link Copied!" : "Copy Download Link"}</div>
              <div className="text-xs text-muted-foreground">
                Anyone with link can download (no login needed)
              </div>
            </div>
          </Button>

          {/* Download Subtitles */}
          {video.srt_url && (
            <Button
              onClick={handleDownloadSubtitles}
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-muted-foreground hover:text-white"
              data-testid="download-subtitles-btn"
            >
              <FileText className="w-4 h-4" />
              <span>Download Subtitles (.srt)</span>
            </Button>
          )}
        </div>

        {/* Mobile-specific tips */}
        {isMobile && (
          <div className="bg-secondary/30 rounded-lg p-3 mb-2">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00F0FF]" />
              <div>
                {isIOS ? (
                  <span>
                    <strong className="text-white">iOS Tip:</strong> After tapping "Save to Photos", 
                    select <strong className="text-[#00FF94]">"Save Video"</strong> from the share menu to save directly to your Camera Roll.
                  </span>
                ) : (
                  <span>
                    <strong className="text-white">Android Tip:</strong> Tap "Save to Photos" and select 
                    your <strong className="text-[#00FF94]">Gallery</strong> or <strong className="text-[#00FF94]">Google Photos</strong> app to save.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Desktop tip */}
        {!isMobile && (
          <p className="text-xs text-muted-foreground text-center pb-2">
            Tip: Share links work without requiring login
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
